import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
} from "recharts";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DisaggregatedMetric,
  DistributionData,
  GeoDetails,
  GeoTag,
  MetricMetadata,
  RawIndicatorData,
} from "../../services/types";
import { formatValue } from "../../utils/helpers";
import {
  fetchDistributionData,
  fetchIndicatorBinCount,
  fetchMetricMetadata,
} from "../../utils/dashboardUtils";
import MetricMetadataModal from "../MetricMetadataModal";
import metricMetadataIcon from "../../assets/metric-metadata-icon-blue.svg";

interface CrimeCategoryChartProps {
    crimeIndexMetric?: DisaggregatedMetric;
    propertyMetric?: DisaggregatedMetric;
    personalMetric?: DisaggregatedMetric;
    societyMetric?: DisaggregatedMetric;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    disaggData: DisaggregatedMetric[];
    title?: string;
    subtitle?: string;
    height?: string;
    showMetadataIcon?: boolean;
}

type CrimeRow = {
    name: string;
    value: number;
    percent: number;
    metric?: DisaggregatedMetric;
};

const BAR_COLOR = "#1F3B73";

const getMetricValue = (metric?: DisaggregatedMetric) => {
    return metric?.versions?.[0]?.value ?? 0;
};

const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const row = payload[0]?.payload;

    return (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm px-3 py-2 text-xs">
        <div className="font-medium text-slate-800">{row.name}</div>
        <div className="text-slate-600">
            Count: {formatValue(row.value, "integer")}
        </div>
        <div className="text-slate-600">
            Share: {row.percent.toFixed(1)}%
        </div>
        </div>
    );
};

const ValueLabel = (props: any) => {
    const { x, y, width, height, value } = props;

    return (
        <text
        x={x + width + 8}
        y={y + height / 2}
        fill="#4B5563"
        fontSize={12}
        fontWeight={600}
        textAnchor="start"
        dominantBaseline="middle"
        >
        {formatValue(value, "integer")}
        </text>
    );
};

const CrimeCategoryChart = ({
    crimeIndexMetric,
    propertyMetric,
    personalMetric,
    societyMetric,
    geoDetails,
    geoTag,
    disaggData,
    selectionActive,
    title = "Crime",
    subtitle,
    height = "h-72",
    showMetadataIcon = true,
}: CrimeCategoryChartProps) => {
    const property = getMetricValue(propertyMetric);
    const personal = getMetricValue(personalMetric);
    const society = getMetricValue(societyMetric);

    const isLoaded =
        propertyMetric?.versions?.[0] ||
        personalMetric?.versions?.[0] ||
        societyMetric?.versions?.[0];

    const [showMetadata, setShowMetadata] = useState(false);
    const [activeMetric, setActiveMetric] = useState<DisaggregatedMetric | null>(null);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const clickDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    const activeMetricId = activeMetric?.id;
    const modalId = `metric-metadata-modal-${activeMetricId ?? "crime"}`;

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
        return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
        delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    const openMetadata = async (
        metric?: DisaggregatedMetric,
        triggerEl?: HTMLButtonElement | null
    ) => {
        if (!metric?.id) return;

        triggerRef.current = triggerEl ?? null;

        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
        setActiveMetric(metric);
        setShowMetadata(true);
        setMetadata(null);
        setDistributionData(null);
        setCountyDistributionData(null);
        setLoading(true);
        setError(null);

        const id = metric.id;

        try {
            const [metadataResponse, distributionResponse, countyDistributionResponse] =
            await Promise.all([
                fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),
                fetchWithCache(
                `distData-${id}-${geoDetails.map((d) => d.lod).join(",")}-${geoTag?.type}-${geoTag?.value}`,
                () => fetchIndicatorBinCount(id, geoDetails, geoTag)
                ),
                fetchWithCache(`countyDist-${id}-default`, () =>
                fetchDistributionData(id, [], null)
                ),
            ]);

            setMetadata(metadataResponse?.data || null);
            setDistributionData(distributionResponse || null);
            setCountyDistributionData(countyDistributionResponse || null);
        } catch (err) {
            const errorMessage =
            (err as Error).message || "An unexpected error occurred.";
            setError(new Error(`Failed to load data: ${errorMessage}`));
        } finally {
            setLoading(false);
        }
        }, 150);
    };

    const closeMetadata = () => {
        setShowMetadata(false);
    };

    useEffect(() => {
        return () => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);
        };
    }, []);

    const total = property + personal + society;

    const chartData: CrimeRow[] = useMemo(
        () =>
        [
            {
            name: "Property",
            value: property,
            percent: total ? (property / total) * 100 : 0,
            metric: propertyMetric,
            },
            {
            name: "Personal",
            value: personal,
            percent: total ? (personal / total) * 100 : 0,
            metric: personalMetric,
            },
            {
            name: "Society",
            value: society,
            percent: total ? (society / total) * 100 : 0,
            metric: societyMetric,
            },
        ].sort((a, b) => b.value - a.value),
        [property, personal, society, total, propertyMetric, personalMetric, societyMetric]
    );

    if (!isLoaded) {
        return <Skeleton height={260} />;
    }

    return (
        <>
        <div 
		    className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white"
	    >
            <div className="p-3">
            <div className="flex justify-between items-center gap-3">
                <div>
                <h3 className="text-sm font-semibold text-port-text">{title}</h3>
                {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
                </div>

                {showMetadataIcon && crimeIndexMetric?.id && (
                <button
                    type="button"
                    onClick={(e) => openMetadata(crimeIndexMetric, e.currentTarget)}
                    aria-haspopup="dialog"
                    aria-expanded={showMetadata ? "true" : "false"}
                    aria-controls={modalId}
                    aria-label={`Additional information about ${crimeIndexMetric.name}`}
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded transition-transform hover:scale-105 focus:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-900"
                >
                    <img
                    src={metricMetadataIcon}
                    alt=""
                    aria-hidden="true"
                    className="h-3 w-3"
                    draggable="false"
                    />
                </button>
                )}
            </div>
            </div>

            <div className={`${height} px-4 pb-2`}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 8, right: 56, left: 24, bottom: 8 }}
                barCategoryGap="26%"
                >
                <XAxis type="number" hide />
                <YAxis
                    type="category"
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#4B5563" }}
                    width={72}
                />
                <Tooltip cursor={false} content={<CustomTooltip />} />
                <Bar
                    dataKey="value"
                    radius={[0, 4, 4, 0]}
                    barSize={14}
                >
                    {chartData.map((entry) => (
                    <Cell key={entry.name} fill={BAR_COLOR} />
                    ))}
                    <LabelList dataKey="value" content={<ValueLabel />} />
                </Bar>
                </BarChart>
            </ResponsiveContainer>
            </div>

            <div className="px-4 pb-4 pt-2 border-t-2 border-slate-200">
            <div className="text-xs font-medium text-slate-800">
                Total: {formatValue(total, "integer")}
            </div>

            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-500">
                {chartData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                    <span>
                    <span className="font-medium text-slate-700">{item.name}</span>{" "}
                    {item.percent.toFixed(1)}%
                    </span>

                    {showMetadataIcon && item.metric?.id && (
                    <button
                        type="button"
                        onClick={(e) => openMetadata(item.metric, e.currentTarget)}
                        aria-haspopup="dialog"
                        aria-expanded={showMetadata ? "true" : "false"}
                        aria-controls={modalId}
                        aria-label={`Additional information about ${item.metric.name}`}
                        className="inline-flex h-4 w-4 items-center justify-center rounded transition-transform hover:scale-105 focus:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-900"
                    >
                        <img
                        src={metricMetadataIcon}
                        alt=""
                        aria-hidden="true"
                        className="h-3 w-3"
                        draggable="false"
                        />
                    </button>
                    )}
                </div>
                ))}
            </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

            {activeMetricId !== undefined && (
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={closeMetadata}
                id={activeMetricId}
                metadata={metadata}
                disaggData={disaggData}
                stateData={null}
                nationalData={null}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
            )}
        </>
    );
};

export default CrimeCategoryChart;